import { IDEOLOGY_AXES } from '@/constants/ideology'
import { getPositionLabel } from '@/lib/ideology'
import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'

interface IdeologyDisplayProps {
  ideology: IdeologyVector
  /** Whether to show position labels (e.g., "Strongly Free Market") */
  showLabels?: boolean
  /** Compact mode for smaller displays */
  compact?: boolean
  className?: string
}

/**
 * Visual representation of a 7-axis ideology vector using horizontal bars.
 * Center represents 0 (neutral), left is -100, right is +100.
 */
export function IdeologyDisplay({
  ideology,
  showLabels = true,
  compact = false,
  className,
}: IdeologyDisplayProps) {
  return (
    <div className={cn('space-y-3', compact && 'space-y-2', className)}>
      {IDEOLOGY_AXES.map((axis, index) => {
        const value = ideology[index]
        const positionLabel = getPositionLabel(index, value)

        return (
          <IdeologyAxisBar
            key={axis.key}
            label={axis.label}
            leftLabel={axis.leftLabel}
            rightLabel={axis.rightLabel}
            value={value}
            positionLabel={positionLabel}
            showLabels={showLabels}
            compact={compact}
          />
        )
      })}
    </div>
  )
}

interface IdeologyAxisBarProps {
  label: string
  leftLabel: string
  rightLabel: string
  value: number
  positionLabel: string
  showLabels: boolean
  compact: boolean
}

function IdeologyAxisBar({
  label,
  leftLabel,
  rightLabel,
  value,
  positionLabel,
  showLabels,
  compact,
}: IdeologyAxisBarProps) {
  // Convert -100 to +100 value to a 0-100 percentage for bar positioning
  const percentage = ((value + 100) / 200) * 100

  // Determine color based on value
  const getBarColor = (val: number) => {
    if (val < -40) return 'bg-red-500'
    if (val < -10) return 'bg-red-400'
    if (val <= 10) return 'bg-gray-400'
    if (val <= 40) return 'bg-blue-400'
    return 'bg-blue-500'
  }

  return (
    <div className={cn('space-y-1', compact && 'space-y-0.5')}>
      {/* Axis label and value */}
      <div className="flex items-center justify-between">
        <span className={cn('font-medium text-sm', compact && 'text-xs')}>
          {label}
        </span>
        <span className={cn(
          'text-xs tabular-nums',
          value > 0 && 'text-blue-600',
          value < 0 && 'text-red-600',
          value === 0 && 'text-muted-foreground'
        )}>
          {value > 0 ? '+' : ''}{value}
        </span>
      </div>

      {/* Bar visualization */}
      <div className="relative">
        {/* Left/Right labels */}
        {!compact && (
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>{leftLabel}</span>
            <span>{rightLabel}</span>
          </div>
        )}

        {/* Bar track */}
        <div className={cn(
          'relative h-2 bg-muted rounded-full overflow-hidden',
          compact && 'h-1.5'
        )}>
          {/* Center line marker */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border z-10" />

          {/* Value indicator bar */}
          {value !== 0 && (
            <div
              className={cn(
                'absolute top-0 bottom-0 transition-all',
                getBarColor(value)
              )}
              style={{
                left: value < 0 ? `${percentage}%` : '50%',
                width: `${Math.abs(value) / 2}%`,
              }}
            />
          )}

          {/* Position marker dot */}
          <div
            className={cn(
              'absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-background z-20',
              getBarColor(value)
            )}
            style={{ left: `calc(${percentage}% - 4px)` }}
          />
        </div>
      </div>

      {/* Position label */}
      {showLabels && !compact && (
        <div className="text-xs text-muted-foreground text-center">
          {positionLabel}
        </div>
      )}
    </div>
  )
}

/**
 * Compact single-line ideology indicator for cards
 */
interface IdeologyMiniProps {
  ideology: IdeologyVector
  className?: string
}

export function IdeologyMini({ ideology, className }: IdeologyMiniProps) {
  // Show just small dots representing each axis position
  return (
    <div className={cn('flex gap-0.5 items-center', className)}>
      {IDEOLOGY_AXES.map((axis, index) => {
        const value = ideology[index]
        return (
          <div
            key={axis.key}
            className={cn(
              'w-1.5 h-1.5 rounded-full',
              value < -30 && 'bg-red-500',
              value >= -30 && value < 0 && 'bg-red-300',
              value >= 0 && value <= 30 && 'bg-gray-300',
              value > 30 && value <= 60 && 'bg-blue-300',
              value > 60 && 'bg-blue-500',
            )}
            title={`${axis.label}: ${value}`}
          />
        )
      })}
    </div>
  )
}
